/**
 * The tax categories (decisions 0086, 0742, 0900): what a line is, never
 * whether it is taxed. 🔒 A state's `TaxCategoryStateRule` is the one place a
 * state answers for a category, from its own date, with one of four
 * treatments: NotTaxable, Default, Taxable or Custom (decision 0904).
 *
 * The owner, T286: *"I normally do goods and service. since at least in pa
 * service is not taxable and goods are unless they are like food. That is very
 * confusing"*. And on the research that found a quarter of the eight
 * categories' answers were neither yes nor no, 2026-09-25: *"Doesn't it make
 * more sense to create more tax categories [than] complicating the ones that
 * are there?"* Each row carries a one-line hint, stored in the row's `notes?`,
 * which the Item form draws under the chosen category.
 */

export interface TaxCategorySeedRow {
  name: string;
  sortOrder: number;
  /** The one-line hint the Item form draws under the chosen category. */
  hint: string;
  isActive: boolean;
}

/** 🔨 Retired from every choice list and kept for the finance-charge line and the lines already written on it (decision 0742). */
export const NON_TAXABLE_CATEGORY_NAME = 'Non-taxable';

export const REPAIR_LABOUR_CATEGORY_NAME = 'Repair labor on equipment';
export const ENGINEERING_AND_DESIGN_CATEGORY_NAME = 'Engineering and design';

/** 🔒 The category a shipping charge is billed under, which an edit may move without dropping a carried approval (decision 1690). */
export const FREIGHT_CATEGORY_NAME = 'Freight and delivery';

/** The thirteen categories, in the order a choice list offers them, and Non-taxable retired after them (decision 0900). */
export const TAX_CATEGORY_ROWS: readonly TaxCategorySeedRow[] = [
  { name: 'Goods', sortOrder: 1, hint: 'General tangible property: parts, equipment, materials and anything else you can hold.', isActive: true },
  { name: 'Food and grocery', sortOrder: 2, hint: 'Food and drink sold for eating, which many states tax at a lower rate or not at all.', isActive: true },
  { name: 'Clothing', sortOrder: 3, hint: 'Clothing and footwear, including work wear; some states exempt it below a price.', isActive: true },
  { name: REPAIR_LABOUR_CATEGORY_NAME, sortOrder: 4, hint: 'Repairing or servicing movable equipment, billed on its own line.', isActive: true },
  { name: 'Installation: new construction or capital improvement', sortOrder: 5, hint: 'Work that becomes a permanent part of a building, in new construction or as a capital improvement.', isActive: true },
  { name: 'Installation: repair, maintenance or remodel of a building', sortOrder: 6, hint: 'Repair, maintenance, alteration or remodel of an existing building.', isActive: true },
  { name: 'Security, alarm and fire systems', sortOrder: 7, hint: 'Installing, programming and monitoring security, burglar, fire and alarm systems.', isActive: true },
  { name: ENGINEERING_AND_DESIGN_CATEGORY_NAME, sortOrder: 8, hint: 'Engineering, design and consulting sold on their own, apart from programming.', isActive: true },
  { name: 'Controls programming and IT services', sortOrder: 9, hint: 'Programming building-control systems, software configuration, IT support, data processing and hosting services.', isActive: true },
  { name: 'Prewritten software', sortOrder: 10, hint: 'Canned software by download or on media.', isActive: true },
  { name: 'Custom software', sortOrder: 11, hint: 'Software written for one customer.', isActive: true },
  { name: 'Hosted software and subscriptions', sortOrder: 12, hint: 'SaaS and other hosted access and subscriptions.', isActive: true },
  { name: FREIGHT_CATEGORY_NAME, sortOrder: 13, hint: 'Shipping, freight and delivery charges billed to the customer.', isActive: true },
  { name: NON_TAXABLE_CATEGORY_NAME, sortOrder: 99, hint: 'Retired: kept for finance charges and older lines. Choose what the thing is instead.', isActive: false },
];

/**
 * 🔨 The four of decision 0742's eight renamed in place by a migration
 * (decision 0900), so every Item, line and rule row keeps its reference. The
 * seed writes by name, so a rename by seed alone would write a second row.
 */
export const TAX_CATEGORY_RENAMES: readonly { readonly from: string; readonly to: string }[] = [
  { from: 'Repair labour on tangible property', to: REPAIR_LABOUR_CATEGORY_NAME },
  { from: 'Installation labour on real property', to: 'Installation: new construction or capital improvement' },
  { from: 'Professional service', to: ENGINEERING_AND_DESIGN_CATEGORY_NAME },
  { from: 'Software and subscriptions', to: 'Prewritten software' },
];

/**
 * 🔨 An Item's kind chooses its category where none is named (decisions 0742,
 * 0900): a Good takes Goods and a Service takes Engineering and design, the row
 * Professional service became, the reading the product owner approved on
 * 2026-09-25. An Item carries two kinds, so a labour item and an engineering
 * service cannot be told apart; a labour item is moved to Repair labour on
 * equipment by hand, and the open issue in piece 25 asks whether the Item gains
 * a third kind.
 */
export const TAX_CATEGORY_DEFAULT_BY_ITEM_TYPE: Readonly<Record<'Good' | 'Service', string>> = {
  Good: 'Goods',
  Service: ENGINEERING_AND_DESIGN_CATEGORY_NAME,
};
